import { useCallback } from 'react';

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  InfiniteData,
  QueryClient,
  keepPreviousData,
  useQuery,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { JsonValue } from 'type-fest';

import { insightsEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import useHomeCheckList, { AccountUserPrefsEnum } from '@/hooks/useHomeCheckList';
import { useInsightFilter } from '@/modules/insights/contexts/InsightFilterContext';
import { selectCurrentInsightId } from '@/redux/insights/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { Pagination } from '@/shared/types/common';
import type { SortDirection } from '@/shared/types/common';
import { Insight, Insights } from '@/shared/types/insights';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

interface InsightsWithPagination extends Insights, Pagination {}

export interface TestInsightResponse {
  value: JsonValue;
}

export const API = Object.freeze({
  listInsights: async (
    brainId: string,
    pageParam?: { cursor?: string },
    filter?: string,
    nameDirection?: SortDirection,
    typeDirection?: SortDirection,
    updatedDirection?: SortDirection
  ): Promise<InsightsWithPagination> => {
    return callGet(
      endpoints.insights(
        brainId,
        pageParam?.cursor,
        filter,
        nameDirection,
        typeDirection,
        updatedDirection
      )
    );
  },
  getInsight: async (brainId: string, insightId: string): Promise<Insight> =>
    callGet(endpoints.insight(brainId, insightId)),
  createInsight: async (brainId: string, data: Partial<Insight>): Promise<Insight> =>
    callPost(endpoints.insights(brainId), data),
  updateInsight: async (
    brainId: string,
    insightId: string,
    data: Partial<Insight>
  ): Promise<Insight> => callPut(endpoints.insight(brainId, insightId), data),
  deleteInsight: async (brainId: string, insightId: string): Promise<Pick<Insight, 'insight_id'>> =>
    callDelete(endpoints.insight(brainId, insightId)),
  testInsight: async (
    brainId: string,
    data: { session_id: string; insight: Partial<Insight> }
  ): Promise<TestInsightResponse> => {
    return callPut(endpoints.testInsight(brainId), data);
  },
});

/**
 * Updates insight data in all relevant query caches
 * @param queryKey - The query key used to identify the insights queries
 * @param queryClient - The React Query client instance
 * @param brainId - ID of the brain containing the insight
 * @param insight - Updated insight data
 */
export const onInsightUpdated = (
  queryKey: string[],
  queryClient: QueryClient,
  brainId: string,
  insight: Insight
) => {
  // Update individual insight cache if it exists
  queryClient.setQueryData<Insight>(
    ['insight', brainId, insight.insight_id],
    (prev: Insight | undefined) => {
      if (!prev) {
        return insight;
      }
      return { ...prev, ...insight };
    }
  );

  // Update function to apply to all matching caches
  const updateInsightInCache = (prev: InfiniteData<InsightsWithPagination> | undefined) => {
    if (!prev) {
      return prev;
    }

    return {
      ...prev,
      pages: prev.pages.map((page) => ({
        ...page,
        insights: page.insights.map((ins) =>
          ins.insight_id === insight.insight_id ? insight : ins
        ),
      })),
    };
  };

  // Find and update all insights queries for this brain
  queryClient
    .getQueryCache()
    .findAll({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === 'insights' &&
          key[1] === brainId &&
          key[2] === queryKey[2] &&
          key[3] === queryKey[3] &&
          key[4] === queryKey[4] &&
          key[5] === queryKey[5]
        );
      },
    })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, updateInsightInCache);
    });
};

/**
 * Adds a new insight to all relevant query caches
 * @param queryClient - The React Query client instance
 * @param brainId - ID of the brain containing the insight
 * @param insight - New insight data
 */
export const onInsightCreated = (queryClient: QueryClient, brainId: string, insight: Insight) => {
  queryClient.invalidateQueries({ queryKey: ['all-insights', brainId] });

  const addInsightToCache = (prev: InfiniteData<InsightsWithPagination> | undefined) => {
    if (!prev) {
      return prev;
    }

    const newPages = prev.pages.map((page, index) => {
      if (index === 0) {
        return {
          ...page,
          pagination: { ...page.pagination, total: (page.pagination?.total || 0) + 1 },
          insights: [
            insight,
            ...page.insights.filter((ins) => ins.insight_id !== insight.insight_id),
          ],
        };
      }
      return page;
    });

    return { ...prev, pages: newPages };
  };

  // Find and update all insights queries for this brain
  queryClient
    .getQueryCache()
    .findAll({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'insights' && key[1] === brainId;
      },
    })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, addInsightToCache);
    });
};

/**
 * Removes an insight from all relevant query caches
 * @param queryKey - The query key used to identify the insights queries
 * @param queryClient - The React Query client instance
 * @param brainId - ID of the brain containing the insight
 * @param insightId - ID of the insight to remove
 */
export const onInsightRemoved = (queryClient: QueryClient, brainId: string, insightId: string) => {
  queryClient.invalidateQueries({ queryKey: ['all-insights', brainId] });
  // Update function to apply to all matching caches
  const removeInsightFromCache = (prev: InfiniteData<InsightsWithPagination> | undefined) => {
    if (!prev) {
      return prev;
    }

    return {
      ...prev,
      pages: prev.pages.map((page, index) => {
        if (index === 0) {
          return {
            ...page,
            pagination: {
              ...page.pagination,
              total: Math.max(0, (page.pagination?.total || 0) - 1),
            },
            insights: page.insights.filter((ins) => ins.insight_id !== insightId),
          };
        }
        return { ...page, insights: page.insights.filter((ins) => ins.insight_id !== insightId) };
      }),
    };
  };

  // Find and update all insights queries for this brain
  queryClient
    .getQueryCache()
    .findAll({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'insights' && key[1] === brainId;
      },
    })
    .forEach((query) => {
      queryClient.setQueryData(query.queryKey, removeInsightFromCache);
    });

  // Remove individual insight query
  queryClient.removeQueries({ queryKey: ['insight', brainId, insightId] });
};

/**
 * Custom hook to fetch all insights for a brain without pagination
 * @param brainId - ID of the brain to fetch insights for
 * @returns Query result containing all insights
 */
export const useAllInsights = (brainId: string) => {
  const canRead = useSelector((state: RootState) => getPermissions(state, 'brains', actions.READ));

  return useQuery({
    queryKey: ['all-insights', brainId],
    queryFn: async () => {
      const result = await API.listInsights(brainId);
      return result.insights;
    },
    enabled: !!brainId && canRead,
  });
};

/**
 * Custom hook for managing insights with TanStack Query
 * @param brainId - ID of the brain to fetch insights for
 * @returns Object containing insights data and mutation functions
 */
export const useInsights = (brainId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { markAsComplete } = useHomeCheckList();
  const { textFilter, typeDirection, nameDirection, updatedDirection } = useInsightFilter();
  const insightId = useSelector(selectCurrentInsightId);

  const canRead = useSelector((state: RootState) => getPermissions(state, 'brains', actions.READ));
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.DELETE)
  );

  const queryKey = useCallback(
    (brainId: string) => [
      'insights',
      brainId,
      textFilter,
      typeDirection,
      nameDirection,
      updatedDirection,
    ],
    [textFilter, typeDirection, nameDirection, updatedDirection]
  );

  const {
    data: insights,
    status: insightsStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: queryKey(brainId),
    queryFn: ({ pageParam }) =>
      API.listInsights(
        brainId,
        pageParam,
        textFilter,
        nameDirection,
        typeDirection,
        updatedDirection
      ),
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.pagination) {
        return undefined;
      }
      return lastPage.pagination.has_more ? { cursor: lastPage.pagination.next_cursor } : undefined;
    },
    initialPageParam: {} as { cursor?: string },
    enabled: !!brainId && canRead,
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });

  const { mutate: createInsight, status: createStatus } = useMutation<
    Insight,
    Error,
    Partial<Insight>
  >({
    mutationFn: (data: Partial<Insight>) => API.createInsight(brainId, data),
    onSuccess: (newInsight) => {
      onInsightCreated(queryClient, brainId, newInsight);
      // Invalidate all insights queries to trigger revalidation
      queryClient.invalidateQueries({ queryKey: ['insights', brainId] });
      toast.success(t('insights.create_success', { 0: newInsight.name }));
      // Mark checklist item as complete
      markAsComplete(AccountUserPrefsEnum.CREATE_INSIGHT);
    },
    meta: { errorToast: true },
  });

  const {
    mutate: updateInsight,
    status: updateStatus,
    reset: resetUpdateMutation,
  } = useMutation<Insight, Error, { insightId: string; data: Partial<Insight> }>({
    mutationFn: ({ insightId, data }) => API.updateInsight(brainId, insightId, data),
    onSuccess: (updatedInsight) => {
      onInsightUpdated(queryKey(brainId), queryClient, brainId, updatedInsight);
      toast.success(t('insights.update_success'));
    },
    meta: { errorToast: true },
  });

  const {
    mutate: deleteInsight,
    mutateAsync: deleteInsightAsync,
    status: deleteStatus,
  } = useMutation<Pick<Insight, 'insight_id'>, Error, string>({
    mutationFn: (insightId: string) => API.deleteInsight(brainId, insightId),
    onSuccess: (_resp, insightId) => {
      onInsightRemoved(queryClient, brainId, insightId);
      toast.success(t('insights.delete_success'));
    },
    meta: { errorToast: true },
  });

  const totalCount = insights?.pages[0]?.pagination?.total || 0;

  const { mutate: testInsight, status: testStatus } = useMutation<
    TestInsightResponse,
    Error,
    {
      data: { session_id: string; insight: Partial<Insight> };
      onSuccess?: (response: TestInsightResponse) => void;
      onError?: (error: Error) => void;
    }
  >({
    mutationFn: ({ data }) => API.testInsight(brainId, data),
    onSuccess: (response, variables) => {
      variables.onSuccess?.(response);
    },
    onError: (error, variables) => {
      variables.onError?.(error);
    },
  });

  // Fetch individual insight when insight ID is available
  const { data: selectedInsight, status: selectedInsightStatus } = useQuery({
    queryKey: ['insight', brainId, insightId],
    queryFn: () => API.getInsight(brainId, insightId!),
    enabled: !!brainId && !!insightId && canRead,
  });

  return {
    insights: insights?.pages,
    insightsStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    createInsight,
    updateInsight,
    resetUpdateMutation,
    deleteInsight,
    deleteInsightAsync,
    testInsight,
    createStatus,
    updateStatus,
    deleteStatus,
    testStatus,
    canWrite,
    canDelete,
    totalCount,
    selectedInsight,
    selectedInsightStatus,
  };
};
